import { useCallback, useRef, useState } from "react";
import {
  Button,
  Divider,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useObservableState } from "applesauce-react/hooks";
import { Deferred } from "applesauce-core/promise";
import { useActiveAccount } from "applesauce-react/hooks";
import { PasswordSigner } from "applesauce-signers";

import { pinUnlockRequest$ } from "../services/pin-unlock";
import Fingerprint04 from "./icons/fingerprint-04";
import { isPasskeySupported, getPasskeyRegistrations, unlockAnyPasskey } from "../services/passkey";

export default function PinUnlockModal() {
  const defer = useObservableState(pinUnlockRequest$) as Deferred<string> | null;
  const [password, setPassword] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const account = useActiveAccount();

  const handleSubmit = useCallback(() => {
    if (!defer || !password) return;
    setPassword("");
    defer.resolve(password);
  }, [defer, password]);

  const handleCancel = useCallback(() => {
    if (!defer) return;
    setPassword("");
    defer.reject(new Error("Cancelled"));
  }, [defer]);

  const handlePasskey = useCallback(async () => {
    if (!defer || !account) return;
    setPasskeyLoading(true);
    try {
      const { secretKey } = await unlockAnyPasskey();
      // Set the key directly on the signer — PasswordSigner.unlock() will no-op since key is already set
      if (account.signer instanceof PasswordSigner) {
        account.signer.key = secretKey;
      }
      setPassword("");
      defer.resolve("__passkey__");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Passkey unlock failed";
      if (!message.includes("cancelled")) {
        toast({ description: message, status: "error", duration: 5000 });
      }
    } finally {
      setPasskeyLoading(false);
    }
  }, [defer, account, toast]);

  const showPasskey = isPasskeySupported() && getPasskeyRegistrations().length > 0;

  return (
    <Modal
      isOpen={!!defer}
      onClose={handleCancel}
      initialFocusRef={inputRef}
      size="sm"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Unlock Account</ModalHeader>
        <ModalBody>
          {showPasskey && (
            <>
              <Button
                leftIcon={<Fingerprint04 boxSize={5} />}
                onClick={handlePasskey}
                isLoading={passkeyLoading}
                loadingText="Authenticating..."
                colorScheme="purple"
                variant="outline"
                w="full"
                size="lg"
              >
                Unlock with Passkey
              </Button>
              <Flex align="center" gap="3" my="3">
                <Divider />
                <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                  or
                </Text>
                <Divider />
              </Flex>
            </>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Input
              ref={inputRef}
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </form>
        </ModalBody>
        <ModalFooter gap="2">
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button colorScheme="primary" onClick={handleSubmit} isDisabled={!password}>
            Unlock
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
